import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function AdresstwattPricing() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  const features = [
    { key: "statistics", freeAnalysis: true, fullWash: true },
    { key: "identify", freeAnalysis: true, fullWash: true },
    { key: "correct", freeAnalysis: false, fullWash: true },
    { key: "split", freeAnalysis: false, fullWash: true },
    { key: "suggest", freeAnalysis: false, fullWash: true },
    { key: "enrich", freeAnalysis: false, fullWash: true },
  ];

  const tiers = [
    { key: "tier1", range: "1 – 10 000" },
    { key: "tier2", range: "10 001 – 100 000" },
    { key: "tier3", range: "100 001+" },
  ];

  return (
    <section className="py-20 lg:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-coral uppercase tracking-widest text-xs font-semibold font-subheading">
            {t("productPages.adresstwatt.pricing.tagline")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-primary mt-3">
            {t("productPages.adresstwatt.pricing.title")}
          </h2>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            {t("productPages.adresstwatt.pricing.subtitle")}
          </p>
        </div>

        {/* Feature comparison */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-card rounded-2xl border overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="min-w-[200px] font-subheading uppercase tracking-wide text-xs text-muted-foreground">
                    {t("productPages.adresstwatt.pricing.descriptionCol")}
                  </TableHead>
                  <TableHead className="text-center min-w-[130px]">
                    <div className="font-subheading uppercase tracking-wide text-xs text-muted-foreground">
                      {t("productPages.adresstwatt.pricing.freeAnalysis")}
                    </div>
                  </TableHead>
                  <TableHead className="text-center min-w-[130px] bg-coral/5">
                    <div className="font-subheading uppercase tracking-wide text-xs text-coral">
                      {t("productPages.adresstwatt.pricing.fullWash")}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((feature) => (
                  <TableRow key={feature.key}>
                    <TableCell className="text-sm text-foreground">
                      {t(`productPages.adresstwatt.pricing.features.${feature.key}`)}
                    </TableCell>
                    <TableCell className="text-center">
                      {feature.freeAnalysis ? (
                        <CheckCircle2 className="h-5 w-5 text-coral mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center bg-coral/5">
                      <CheckCircle2 className="h-5 w-5 text-coral mx-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Per-row pricing */}
          <div className="mt-8 bg-card rounded-2xl border overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="min-w-[200px] font-subheading uppercase tracking-wide text-xs text-muted-foreground">
                    {t("productPages.adresstwatt.pricing.volumeCol")}
                  </TableHead>
                  <TableHead className="text-center min-w-[130px] font-subheading uppercase tracking-wide text-xs text-muted-foreground">
                    {t("productPages.adresstwatt.pricing.freeAnalysis")}
                  </TableHead>
                  <TableHead className="text-center min-w-[130px] bg-coral/5 font-subheading uppercase tracking-wide text-xs text-coral">
                    {t("productPages.adresstwatt.pricing.fullWash")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Setup fee */}
                <TableRow className="bg-secondary/30">
                  <TableCell className="text-sm font-medium text-foreground">
                    {t("productPages.adresstwatt.pricing.setupFee")}
                  </TableCell>
                  <TableCell className="text-center text-sm font-semibold text-foreground">
                    {t("productPages.adresstwatt.pricing.setupFreeValue")}
                  </TableCell>
                  <TableCell className="text-center text-sm font-semibold text-foreground bg-coral/5">
                    {t("productPages.adresstwatt.pricing.setupWashValue")}
                  </TableCell>
                </TableRow>
                {/* Tiers */}
                {tiers.map((tier) => (
                  <TableRow key={tier.key}>
                    <TableCell className="text-sm text-foreground">
                      {tier.range} {t("productPages.adresstwatt.pricing.rows")}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {t(`productPages.adresstwatt.pricing.tiers.${tier.key}.free`)}
                    </TableCell>
                    <TableCell className="text-center text-sm font-medium text-foreground bg-coral/5">
                      {t(`productPages.adresstwatt.pricing.tiers.${tier.key}.wash`)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild>
              <Link to={getLocalizedPath("/bestall")}>{t("productPages.ctaOrder")}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={getLocalizedPath("/kontakt?type=demo")}>{t("productPages.ctaButton")}</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
